import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import {
  DashboardPeriod,
  ReportedUsageMerchantStat,
} from '../dashboardTypes'

type SplitTone = 'positive' | 'warning' | 'muted' | 'plain'

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  padding: 1rem 1.125rem 1.125rem;
  background: ${palette.white};
`

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: ${palette.grey.grey100};
  color: ${palette.grey.grey700};
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
`

const Splits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.25rem;
`

const Split = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const SplitTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`

const SplitName = styled.span`
  min-width: 0;
  color: ${palette.text.secondary};
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.3;
`

const SplitPill = styled.span<{ $tone: SplitTone }>`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.4;
  background: ${({ $tone }) => {
    if ($tone === 'positive') return palette.success.green100
    if ($tone === 'warning') return palette.warning.yellow100
    return palette.grey.grey100
  }};
  color: ${({ $tone }) => {
    if ($tone === 'positive') return palette.success.green800
    if ($tone === 'warning') return palette.warning.yellow800
    return palette.grey.grey700
  }};
`

const BarRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
`

const BarTrack = styled.div`
  height: 0.25rem;
  width: 100%;
  border-radius: 999px;
  background: ${palette.grey.grey100};
  overflow: hidden;
`

const BarFill = styled.div<{ $width: number }>`
  height: 100%;
  width: ${(props) => props.$width}%;
  border-radius: 999px;
  background: ${palette.grey.grey500};
`

const formatMoney = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`

const formatCount = (value: number) => value.toLocaleString('en-US')

const formatMiles = (miles: number) =>
  `${Math.round(miles).toLocaleString('en-US')} mi`

const splitTone = (name: string): SplitTone => {
  const key = name.trim().toLowerCase()
  if (key === 'active') return 'positive'
  if (key === 'pending') return 'warning'
  if (
    key === 'expired' ||
    key === 'canceled' ||
    key === 'cancelled' ||
    key === 'other'
  ) {
    return 'muted'
  }
  return 'plain'
}

const splitLabel = (name: string, tone: SplitTone) => {
  if (tone === 'plain') return name
  const text = name.trim().toLowerCase()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const ReportedUsageMerchants = ({
  merchants,
}: {
  merchants: ReportedUsageMerchantStat[]
  period: DashboardPeriod
}) => {
  if (!merchants.length) {
    return null
  }
  return (
    <Grid>
      {merchants.map((merchant) => {
        const maxSplit = Math.max(
          ...merchant.splits.map((split) => split.spendCents),
          1
        )
        return (
          <Card key={merchant.merchant}>
            <Heading>
              <Text variant='body1' fontWeight={500} color={palette.text.secondary}>
                {merchant.label}
              </Text>
              <Text variant='h2' fontWeight={600}>
                {formatMoney(merchant.spendCents)}
              </Text>
            </Heading>
            <Pills>
              <Pill>{formatCount(merchant.people)} people</Pill>
              <Pill>{formatCount(merchant.records)} records</Pill>
              {merchant.miles ? <Pill>{formatMiles(merchant.miles)}</Pill> : null}
            </Pills>
            {merchant.splits.length ? (
              <Splits>
                {merchant.splits.map((split) => {
                  const tone = splitTone(split.name)
                  const label = splitLabel(split.name, tone)
                  return (
                    <Split key={split.name}>
                      <SplitTop>
                        {tone === 'plain' ? (
                          <SplitName title={split.name}>{label}</SplitName>
                        ) : (
                          <SplitPill $tone={tone} title={split.name}>
                            {label}
                          </SplitPill>
                        )}
                        <Text variant='body2' color={palette.text.primary}>
                          {formatMoney(split.spendCents)}
                        </Text>
                      </SplitTop>
                      <BarRow>
                        <BarTrack>
                          <BarFill
                            $width={Math.max(
                              Math.round((split.spendCents / maxSplit) * 100),
                              split.spendCents > 0 ? 4 : 0
                            )}
                          />
                        </BarTrack>
                        <Text variant='caption' color={palette.text.secondary}>
                          {formatCount(split.records)}
                        </Text>
                      </BarRow>
                    </Split>
                  )
                })}
              </Splits>
            ) : null}
          </Card>
        )
      })}
    </Grid>
  )
}

export default ReportedUsageMerchants
